import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Observable, of, Subscription, switchMap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SdPlaceVoteComponent } from '../../sd-place-vote/sd-place-vote.component';
import { SdNonEligibleCommentModalComponent } from '../sd-non-eligible-comment-modal/sd-non-eligible-comment-modal.component';
import { materialSymbols } from '../../../../../shared/constants/material-symbols';
import { SdVoteId, sdVoteOption } from '../../../../../shared/constants/sd-ballot.constants';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IApiResponse } from '../../../../../shared/models/api-response';
import { IBlobFile } from '../../../../../shared/models/blob-file';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { FileDownloadUtility } from '../../../../../shared/utilities/blob-download-utility';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { CommitteeService } from '../../../../committees/shared/services/committee.service';
import { DateFormatPipe } from '../../../../controls/pipes/date-format.pipe';
import { SummaryComponentView } from '../../../shared/components/button-vote-now/button-vote-now.component';
import { ISdBallotDetailDto, IBallotStatus } from '../../../shared/models/sd/sd-ballot-detail';
import { IUpdateSdBallotNoteRequest } from '../../../shared/models/sd/update-sd-ballot-note-request';
import { SdBallotService } from '../../../shared/services/sd-ballot.service';
import { ISdVoteDto, RegulatoryAndControllingBodiesVoteCommittee, RegulatoryAndControllingBodiesVote } from '../../../shared/models/sd/sd-vote';


@Component({
    selector: 'app-sd-summary',
    templateUrl: './sd-summary.component.html',
    styleUrl: './sd-summary.component.scss',
    standalone: false
})
export class SdSummaryComponent implements OnInit, OnDestroy, OnChanges {

  @Input() sdBallotDetail?: ISdBallotDetailDto
  @Output() onFinalisedBallot: EventEmitter<number> = new EventEmitter<number>();

  isPmOrAbove: boolean = false;
  currentUser: IUser | null = null;
  subscriptions: Subscription = new Subscription();

  ballotFormGroup = new FormGroup({
      ballotNoteCtrl: new FormControl<string | null>(null),
  });

  ballotVotes: ISdVoteDto[] = [];

  userVote: ISdVoteDto | undefined;
  isVoteButtonEnable: boolean = false;
  hasVoted: boolean = false;
  isVotingActionsVisible: boolean = false;
  isInternalPublicUser: boolean = false;
  openVotePopup: boolean = false;
  isJoint: boolean = false;
  showCommentBtn: boolean = false;
  nonEligibleUserRole: boolean = false;

  agree = materialSymbols.logout;
  disagree = materialSymbols.checkCircle;
  noResponse = materialSymbols.refresh;

  userVoteOption : any;
  userVoteSubmittedDate: string | undefined;
  projectId: string = '';
  designation: string = '';

  draftDocument!: IBlobFile;

  constructor(
    private _authService: AuthService,
    private _sdBallotService: SdBallotService,
    private _committeeService: CommitteeService,
    private _router: ActivatedRoute,
    private _modalService: NgbModal,
    private dateFormatPipe: DateFormatPipe
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    if(this.sdBallotDetail) {
      this.projectId = this.sdBallotDetail?.projectId.toString();
      this.designation = this.sdBallotDetail?.designation;
    }
    this.setupCurrentUserSubscription();
    this.setupUpdateBallotNoteSubscription();
    this.getBallotVotes(this.sdBallotDetail?.projectId??0);
    this.setViewRedirection();
    this.loadDraft();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["sdBallotDetail"] && changes["sdBallotDetail"].currentValue) {
      this.loadBallotNote(changes["sdBallotDetail"].currentValue.notes);
    }
  }

  setViewRedirection() {
    const voteView = this._router.snapshot.queryParamMap.get(SummaryComponentView.vote);
    if(voteView) this.openVotePopup = true;
  }

  get ballotNoteCtrl() {
    return this.ballotFormGroup.get('ballotNoteCtrl');
  }

  setupCurrentUserSubscription(): void {
    this.subscriptions.add(this._authService.currentUserSubject.subscribe({
      next: (user) => {
        if(user) {
          this.currentUser = user;
          this.isPmOrAbove = UserUtility.userIsPmOrAbove(user);
          this.isInternalPublicUser =  UserUtility.isInternalPublicUser(user);
        }
        if (!this.isPmOrAbove) {
          this.ballotNoteCtrl?.disable();
        } else {
          this.ballotNoteCtrl?.enable();
        }
      }
    }));
  }

  setupUpdateBallotNoteSubscription(): void {
    this.subscriptions.add(this.ballotFormGroup.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged((prev, curr) => prev.ballotNoteCtrl === curr.ballotNoteCtrl),
      switchMap((formData: Partial<IFormData>) => this.saveBallotNote(formData))
    ).subscribe({
      next: (apiResponse) => {
        if(!apiResponse || !apiResponse.success) {
          console.log(`Failed to save ballot note. Error: ${apiResponse?.message}`);
        }
    }}));
  }

  loadBallotNote(ballotNote: string | null): void {
    this.ballotFormGroup.patchValue({
      ballotNoteCtrl: ballotNote
    });
  }

  saveBallotNote(formValue: Partial<IFormData>): Observable<IApiResponse<number>> {
    var request: IUpdateSdBallotNoteRequest = {
      ballotId: this.sdBallotDetail?.ballotId || 0,
      note: formValue.ballotNoteCtrl === "" ? null : formValue.ballotNoteCtrl
    }

    var obs$ = this._sdBallotService.updateBallotNote(request);
    return obs$;
  }

  getBallotVotes(projectId: number): void {
    this._sdBallotService.getVotes(projectId).subscribe(
      response => {
          this.ballotVotes = response?.content;
          if (this.ballotVotes && this.ballotVotes.length > 0) {
              this.checkUserEligible();
          }
          this.setVoteWeight();

          this.initVoteButton();

      },
      error => {
        console.error('Error updating ballot details:', error);
      }
    );
  }


  initVoteButton() {
    this.userVote = this.ballotVotes?.find(vote => vote.email == this.currentUser?.email);
    if (!this.userVote) return;

    this.hasVoted = this.userVote?.voteId != SdVoteId.VT_NONE;
    this.isVoteButtonEnable = this.sdBallotDetail?.ballotStatusId == IBallotStatus.PBS_OPEN;
    this.userVoteOption = sdVoteOption[this.userVote?.voteId as SdVoteId??""];
    this.userVoteSubmittedDate = this.dateFormatPipe.transform(this.userVote?.lastUpdated);
    this.isVotingActionsVisible = this.userVote?.isEligible??false;

    this.redirectToVoteIfRequired();
  }

  redirectToVoteIfRequired() {

    if (this.openVotePopup) {
      this.openVotePopup = false;
      this.openPlaceVoteModal();
    }
  }

  refreshVoteSection(voteOption: string) {
    if (voteOption in SdVoteId) {
      this.hasVoted = true;
      this.userVoteOption = sdVoteOption[voteOption as SdVoteId];
      this.userVoteSubmittedDate = this.dateFormatPipe.transform(new Date());
    }
  }

  openNonEligibleCommentModal() {
    const modalRef = this._modalService.open(SdNonEligibleCommentModalComponent,  { windowClass: 'place-vote-custom-modal', centered: true});
    modalRef.componentInstance.userVote = this.userVote;
    modalRef.result.then((result) => {
      console.log('Modal closed with:', result);
    }, () => {
       this.getBallotVotes(this.sdBallotDetail?.projectId??0);
    });
  }

  openPlaceVoteModal() {
    const modalRef = this._modalService.open(SdPlaceVoteComponent,  { windowClass: 'place-vote-custom-modal', centered: true});
    modalRef.componentInstance.userVote = this.userVote;
    modalRef.componentInstance.user = this.currentUser;

    modalRef.result.then((result) => {
      console.log('Modal closed with:', result);
    }, (vote) => {
      if(vote) {
        this.refreshVoteSection(vote);
        this.getBallotVotes(this.sdBallotDetail?.projectId??0);
      }
    });

  }

  checkUserEligible(){
    const isOpenForVoting = this.sdBallotDetail?.unifiedBallotStatusId === UnifiedBallotStatus.openForVoting;
    const userEmail = this.currentUser?.email;
    if(userEmail){
      const userVote = this.ballotVotes?.find(v => v.email === userEmail);
      if(userVote){
        const userNotEligible = userVote.isEligible === false;
        const userRole = userVote.role;
        this.nonEligibleUserRole = UserUtility.isNonEligibleSdRole(userRole);
        this.showCommentBtn = userNotEligible && isOpenForVoting && this.nonEligibleUserRole;
      }
    }
  }

  loadDraft() {
    this._sdBallotService.getDraftFiles(this.sdBallotDetail?.ballotId??0)
        .subscribe({
          next: (response: IApiResponse<IBlobFile>) => {
            if(response.success && response.content) {
              this.draftDocument = response.content;
            }
          },
          error: (error) => {
            // Log to sumologic
          }
    });
  }

  downloadDraft() {
    if (this.draftDocument) {
      FileDownloadUtility.downloadBlobToBrowser(this.draftDocument);
    }
  }

  setVoteWeight() {
    if (this.sdBallotDetail?.committeeCode == RegulatoryAndControllingBodiesVoteCommittee) {
      this._committeeService
      .getCommittee(this.sdBallotDetail?.committeeCode??"")
      .subscribe({
        next: response => {
          if (response.success) {
            const committee = response.content;

            let committeeMembers : string[] = committee.membershipDetails.filter(m => m.stakeholderInterestCategory == RegulatoryAndControllingBodiesVote).map(m => m.memberEmail);

            this.ballotVotes = this.ballotVotes.map(vote => {return {...vote, weight: committeeMembers?.includes(vote.email) ? 0.25 : 1 }});

          }
          this.initVoteButton();
        },
        error: _ => {
        }
      });
    } else {
      this.ballotVotes = this.ballotVotes.map(vote => ({...vote, weight: 1}))
    }
  }
}

interface IFormData {
  ballotNoteCtrl: string | null;
}

